// Database types for Supabase tables
// These types mirror the database schema defined in supabase/schemas/0001_properties.sql

// ============================================================================
// ENUM TYPES
// ============================================================================

export type PropertyStatus =
  | 'new_launch'
  | 'selling_fast'
  | 'just_revealed'
  | 'upcoming'
  | 'limited_availability'
  | 'premium'
  | 'exclusive'
  | 'sold_out';

export type PropertyType =
  | 'apartments'
  | 'villas'
  | 'penthouses'
  | 'duplexes'
  | 'residences'
  | 'townhouses';

// ============================================================================
// DATABASE ROW TYPES
// ============================================================================

export interface Community {
  id: number;
  name: string;
  city: string;
  description: string | null;
  image_url: string | null;
  highlights: string[];
  latitude: number | null;
  longitude: number | null;
  created_at: string;
}

export interface Property {
  id: number;
  name: string;
  community_id: number | null;
  location_name: string;
  price_from: number;
  currency: string;
  status: PropertyStatus;
  type: PropertyType;
  bedrooms: number;
  bathrooms: number | null;
  area_sqft: number | null;
  description: string | null;
  hero_image_url: string;
  brochure_url: string | null;
  latitude: number | null;
  longitude: number | null;
  is_featured: boolean;
  is_published: boolean;
  estimated_completion: string | null;
  created_at: string;
  updated_at: string;
}

export interface PropertyImage {
  id: number;
  property_id: number;
  image_url: string;
  alt_text: string | null;
  display_order: number;
}

export interface PropertyFloorPlan {
  id: number;
  property_id: number;
  label: string;
  title: string;
  specs: string[];
  area_sqft: number | null;
  image_url: string | null;
  display_order: number;
}

export interface Amenity {
  id: number;
  name: string;
  icon: string;
  category: string | null;
}

export interface PropertyAmenity {
  property_id: number;
  amenity_id: number;
}

export interface PropertyDistance {
  id: number;
  property_id: number;
  landmark_name: string;
  icon: string;
  distance_time: string;
  display_order: number;
}

// ============================================================================
// EXTENDED TYPES WITH RELATIONS
// ============================================================================

/**
 * Property with all related data for detail pages
 */
export interface PropertyWithRelations extends Property {
  community: Community | null;
  images: PropertyImage[];
  floor_plans: PropertyFloorPlan[];
  amenities: Amenity[];
  distances: PropertyDistance[];
}

/**
 * Property with community for listing pages
 */
export interface PropertyWithCommunity extends Property {
  community: Community | null;
}

// ============================================================================
// INSERT TYPES (for creating new records)
// ============================================================================

export type CommunityInsert = Omit<Community, 'id' | 'created_at'>;

export type PropertyInsert = Omit<Property, 'id' | 'created_at' | 'updated_at'>;

export type PropertyImageInsert = Omit<PropertyImage, 'id'>;

export type PropertyFloorPlanInsert = Omit<PropertyFloorPlan, 'id'>;

export type AmenityInsert = Omit<Amenity, 'id'>;

export type PropertyDistanceInsert = Omit<PropertyDistance, 'id'>;

// ============================================================================
// UPDATE TYPES (for updating existing records)
// ============================================================================

export type CommunityUpdate = Partial<CommunityInsert>;

export type PropertyUpdate = Partial<PropertyInsert>;

export type PropertyImageUpdate = Partial<PropertyImageInsert>;

export type PropertyFloorPlanUpdate = Partial<PropertyFloorPlanInsert>;

export type AmenityUpdate = Partial<AmenityInsert>;

export type PropertyDistanceUpdate = Partial<PropertyDistanceInsert>;

// ============================================================================
// HOMEPAGE TYPES
// ============================================================================

/**
 * Homepage content (singleton table)
 */
export interface HomepageContent {
  id: number;
  hero_title: string;
  hero_subtitle: string;
  hero_video_url: string | null;
  art_eyebrow: string | null;
  art_title: string | null;
  art_subtitle: string | null;
  art_description: string | null;
  art_image_url: string | null;
  art_cta: string | null;
  brand_title: string | null;
  brand_subtitle: string | null;
  brand_description: string | null;
  brand_image_url: string | null;
  brand_eyebrow: string | null;
  brand_cta: string | null;
  brand_architect_1_name: string | null;
  brand_architect_1_role: string | null;
  brand_architect_2_name: string | null;
  brand_architect_2_role: string | null;
  // Latest Launches section
  launches_eyebrow: string | null;
  launches_title: string | null;
  launches_subtitle: string | null;
  launches_description: string | null;
  // Communities Showcase section
  communities_eyebrow: string | null;
  communities_title: string | null;
  communities_subtitle: string | null;
  communities_description: string | null;
  updated_at: string;
}

/**
 * Manifesto/values items for homepage
 */
export interface ManifestoItem {
  id: number;
  title: string;
  description: string;
  image_url: string | null;
  display_order: number;
  created_at: string;
}

/**
 * Site-wide settings (singleton table)
 */
export interface SiteSettings {
  id: number;
  logo_primary: string;
  logo_secondary: string;
  phone_header: string | null;
  phone_footer: string | null;
  email: string | null;
  address_line1: string | null;
  address_line2: string | null;
  cta_text: string | null;
  footer_title: string | null;
  footer_description: string | null;
  copyright_text: string | null;
  updated_at: string;
}

/**
 * Navigation item for header, footer, or menu
 */
export interface NavigationItem {
  id: number;
  location: 'header' | 'footer' | 'menu';
  label: string;
  href: string;
  parent_id: number | null;
  display_order: number;
  created_at: string;
  // Nested children (populated client-side)
  children?: NavigationItem[];
}

// ============================================================================
// AI SUMMARY TYPES
// ============================================================================

export interface PropertyAiSummaryRow {
  id: number;
  property_id: number;
  summary: {
    headline: string;
    best_for: string;
    location: string;
    community: string;
  };
  model_version: string;
  created_at: string;
}

// ============================================================================
// SEARCH CONTEXT (Natural Language Property Search)
// ============================================================================

export interface SearchContext {
  /** The raw user query */
  query: string;
  /** Parsed intent type */
  intent: 'property_search' | 'browse_all' | 'unsupported' | 'cannot_parse';
  /** Human-readable summary of interpreted criteria */
  explanation: string;
  /** Number of results returned */
  resultCount: number;
  /** Whether the fallback cascade was used */
  wasRelaxed: boolean;
  /** If relaxed, what was adjusted (e.g., "price range adjusted") */
  relaxationNote: string | null;
  /** If intent is unsupported, the reason */
  unsupportedReason: string | null;
  /** Suggested refinement phrases for broad searches */
  suggestedRefinements: string[];
}

// ============================================================================
// FILTER TYPES
// ============================================================================

export interface PropertyFilters {
  type?: PropertyType;
  status?: PropertyStatus;
  bedrooms?: number;
  minPrice?: number;
  maxPrice?: number;
  communityId?: number;
  locationName?: string;
  isFeatured?: boolean;
  search?: string;
}

// ============================================================================
// DISPLAY HELPERS
// ============================================================================

/**
 * Map database status to display label
 */
export const STATUS_LABELS: Record<PropertyStatus, string> = {
  new_launch: 'New Launch',
  selling_fast: 'Selling Fast',
  just_revealed: 'Just Revealed',
  upcoming: 'Upcoming',
  limited_availability: 'Limited Availability',
  premium: 'Premium',
  exclusive: 'Exclusive',
  sold_out: 'Sold Out',
};

/**
 * Map database type to display label
 */
export const TYPE_LABELS: Record<PropertyType, string> = {
  apartments: 'Apartments',
  villas: 'Villas',
  penthouses: 'Penthouses',
  duplexes: 'Duplexes',
  residences: 'Residences',
  townhouses: 'Townhouses',
};

/**
 * Format price for display (e.g., 4500000 -> "From AED 4.5M")
 */
export function formatPrice(priceFrom: number, currency = 'AED'): string {
  if (priceFrom >= 1_000_000) {
    const millions = priceFrom / 1_000_000;
    const formatted = millions % 1 === 0 ? millions.toString() : millions.toFixed(1);
    return `From ${currency} ${formatted}M`;
  }
  if (priceFrom >= 1_000) {
    const thousands = priceFrom / 1_000;
    return `From ${currency} ${thousands}K`;
  }
  return `From ${currency} ${priceFrom}`;
}

/**
 * Result of parsing a price string. May be a single value or a range.
 */
export interface ParsedPrice {
  /** The parsed numeric value (first value if range) */
  value: number;
  /** For ranges, the end value */
  endValue?: number;
  /** Whether this was parsed as a range */
  isRange: boolean;
}

// Strict regex for valid numbers: optional sign, digits, at most one decimal point
const STRICT_NUMBER_PATTERN = /^[+-]?\d+(?:\.\d+)?$/;

/**
 * Parse a price range string into start and end values.
 * Returns null if either side of the range is invalid.
 */
function parseRangePrice(
  rangeMatch: RegExpMatchArray
): ParsedPrice | null {
  const startValue = parseSinglePrice(rangeMatch[1], STRICT_NUMBER_PATTERN);
  const endValue = parseSinglePrice(rangeMatch[2], STRICT_NUMBER_PATTERN);
  
  // Both sides must be valid for a range to be returned
  if (startValue === null || endValue === null) {
    return null;
  }
  
  return {
    value: startValue,
    endValue,
    isRange: true,
  };
}

/**
 * Parse price string to numeric value (e.g., "From AED 4.5M" -> 4500000)
 * 
 * Behavior:
 * - Returns null for invalid/unmatched input (not 0)
 * - Supports K/M suffix multipliers (case-insensitive)
 * - For ranges (containing "-" or "to"), returns null if either side is invalid
 * - Uses strict numeric validation (only one decimal point allowed)
 * 
 * @param priceString - The price string to parse
 * @returns ParsedPrice object with value(s), or null if invalid
 */
export function parsePrice(priceString: string): ParsedPrice | null {
  if (!priceString || typeof priceString !== 'string') {
    return null;
  }

  const trimmed = priceString.trim();
  if (!trimmed) {
    return null;
  }

  // Check for range patterns (e.g., "4.5M - 6M" or "4.5M to 6M")
  const rangeMatch = trimmed.match(/(.+?)(?:\s*[-–—]\s*|\s+to\s+)(.+)/i);
  
  if (rangeMatch) {
    return parseRangePrice(rangeMatch);
  }

  // Single value
  const value = parseSinglePrice(trimmed, STRICT_NUMBER_PATTERN);
  if (value === null) {
    return null;
  }

  return {
    value,
    isRange: false,
  };
}

/**
 * Parse a single price value with optional K/M suffix
 */
function parseSinglePrice(input: string, strictPattern: RegExp): number | null {
  // Extract numeric part and suffix
  const match = input.match(/([+-]?\d+(?:\.\d+)?)\s*([kKmM])?/);
  if (!match) {
    return null;
  }

  const numericPart = match[1];
  const suffix = (match[2] || '').toLowerCase();

  // Validate numeric part against strict pattern
  if (!strictPattern.test(numericPart)) {
    return null;
  }

  const value = parseFloat(numericPart);
  if (!Number.isFinite(value)) {
    return null;
  }

  // Apply multiplier based on suffix
  switch (suffix) {
    case 'm':
      return value * 1_000_000;
    case 'k':
      return value * 1_000;
    default:
      return value;
  }
}

/**
 * Simple price parser that returns just the numeric value (for backward compatibility)
 * Returns NaN for invalid input.
 */
export function parsePriceValue(priceString: string): number {
  const result = parsePrice(priceString);
  return result?.value ?? NaN;
}
